import type { ReadonlyNonEmptyArray } from '../../../../../../utils'

import { areCybusServersEquals, Capability, type CybusServer, type StatusType } from '../../../../../../domain'

import type { SubscriptionFilterArgs } from '../../../../../../application'

import { mapResourceName, type Resource } from '../../../../mappers'
import {
    type AllResourcesRequestConfig,
    BaseSingleResourceSubscriptionStrategy,
    BaseStatefulPageResourceSubscriptionStrategy,
    type PageResourceSortableColumnsMap,
    type ServiceResourcesRequestConfig,
    type SingleResourceBaseRequest,
} from '../Resource'

const mapCybusServer = (resource: Resource<'Server'>, status: StatusType): CybusServer => ({
    id: resource.resourceId,
    service: resource.serviceId,
    name: mapResourceName(resource.resourceId),
    protocol: resource.protocol,
    status,
})

export class ServersStrategy extends BaseStatefulPageResourceSubscriptionStrategy<'Server', 'servers'> {
    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = ['service']

    protected override readonly areEquals = areCybusServersEquals

    protected readonly serviceResourcesRequestConfig: ServiceResourcesRequestConfig<'servers'> = {
        capability: Capability.SERVERS_READ,
        path: '/api/v2/services/+/resources',
        method: 'GET',
    }

    protected override readonly allResourcesRequestConfig: AllResourcesRequestConfig<'servers'> = {
        capability: Capability.SERVERS_READ,
        path: '/api/v2/resources',
        method: 'GET',
    }

    protected override readonly resourceCategories: ReadonlyNonEmptyArray<'Server'> = ['Server']

    protected override readonly resourceStatusType = 'servers' as const

    protected override readonly serviceDeviationsType = ['servers', 'servers'] as const

    protected override readonly serverErrorMessage = 'Could not fetch servers'

    protected sortingColumns: PageResourceSortableColumnsMap<'servers'> = {
        id: 'resourceId',
        name: 'resourceName',
        protocol: 'protocol',
        service: 'serviceId',
        status: null,
    }

    protected override mapResource = mapCybusServer

    override readonly requiredPermissions = [Capability.SERVERS_READ]
}

export class ServerStrategy extends BaseSingleResourceSubscriptionStrategy<'Server', 'server'> {
    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = []

    protected override readonly areEquals = areCybusServersEquals

    protected override readonly resourceCategory = 'Server' as const

    protected override readonly notFoundMessage = 'Could not fetch server'

    protected override readonly resourceStatusType = 'server' as const

    protected readonly baseRequest: SingleResourceBaseRequest<'Server'> = {
        capability: Capability.SERVER_READ,
        method: 'GET',
        path: '/api/v2/resources/+',
    }

    protected mapResource = mapCybusServer

    override readonly requiredPermissions = [Capability.SERVER_READ]
}
